// WhatsNewToast — modal that pops on first launch after the app
// version bumps (see lib/appVersion.js). The user lands on a
// one-screen changelog with bullets pulled from APP_RELEASE_NOTES,
// then taps "Got it" to dismiss. Dismiss writes the current version
// to localStorage so the modal stays away until the NEXT bump.
//
// Why a modal and not a corner toast?
//   • Closed-testing reviewers explicitly need to confirm they're on
//     the new build — a sticky toast that auto-dismisses in 5s would
//     scroll off the screen before they read it.
//   • The modal is portal-rendered into body so it sits above the
//     BottomNav, mini-player, and floating FABs — guaranteed read.
//
// First-ever launches: if no LAST_SEEN_VERSION is stored, we DON'T
// pop the modal (we just mark them as having seen it). Otherwise
// brand-new sign-ups would see a "What's new" before they've even
// used the app once.
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, Sparkle, X, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { APP_VERSION, APP_RELEASE_NOTES, LAST_SEEN_VERSION_KEY } from "../lib/appVersion";

export default function WhatsNewToast() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const lastSeen = window.localStorage.getItem(LAST_SEEN_VERSION_KEY);
      if (!lastSeen) {
        // First-ever visit on this device — silently set the marker.
        window.localStorage.setItem(LAST_SEEN_VERSION_KEY, APP_VERSION);
        return;
      }
      if (lastSeen !== APP_VERSION) {
        // Slight delay so the modal doesn't fight with the page's
        // initial render animations.
        setTimeout(() => setOpen(true), 900);
      }
    } catch (_) {
      // localStorage blocked (incognito + cookies disabled). Skip.
    }
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(LAST_SEEN_VERSION_KEY, APP_VERSION); } catch (_) {}
    setOpen(false);
  };

  // One-tap "share this update" — uses the Web Share API on phones
  // (Android pulls up the system sheet → testers can post the update
  // straight to WhatsApp, SMS, Discord, etc.). Falls back to copying
  // a pre-filled blurb to the clipboard on desktop. Closes the modal
  // after the share intent fires so the user doesn't have to dismiss
  // it separately.
  const shareUpdate = async () => {
    const url = "https://holywords144.com";
    const text = `Just got Holy Words v${APP_VERSION} — Bible read-aloud, animated stories, faith films, and live worship 🙌`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Holy Words v${APP_VERSION}`, text, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Update message copied — paste it anywhere!");
      } else {
        // Last-resort prompt so power users on old browsers can still grab it.
        window.prompt("Copy this:", `${text} ${url}`);
      }
      dismiss();
    } catch (e) {
      // User cancelled the share sheet — keep the modal open so they
      // can either tap Got It or try again. Don't toast an error
      // because cancellation isn't a failure.
      if (e && e.name !== "AbortError") {
        try { toast.error("Couldn't share — try again"); } catch (_) {}
      }
    }
  };

  if (!open) return null;
  const notes = APP_RELEASE_NOTES[APP_VERSION] || [];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in"
      data-testid="whats-new-modal"
      onClick={dismiss}
    >
      <div
        className="bg-cream w-full md:max-w-md mx-4 mb-4 md:mb-0 rounded-3xl shadow-2xl border border-cream-300 overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="whats-new-title"
      >
        <div className="bg-forest text-cream px-6 py-5 flex items-start gap-3 relative">
          <div className="w-10 h-10 rounded-full bg-terracotta/90 grid place-items-center shrink-0">
            <Sparkle size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-cream/70">Holy Words update</p>
            <h2
              id="whats-new-title"
              className="font-heading text-2xl md:text-3xl leading-tight mt-0.5"
              data-testid="whats-new-title"
            >
              What's new in v{APP_VERSION}
            </h2>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="whats-new-close"
            aria-label="Close"
            className="w-8 h-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-cream shrink-0"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <ul className="px-6 py-5 space-y-3" data-testid="whats-new-notes">
          {notes.map((line, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-ink leading-relaxed">
              <CheckCircle size={18} weight="duotone" className="text-forest shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <div className="px-6 pb-5 pt-1 flex flex-col gap-2">
          <button
            type="button"
            onClick={shareUpdate}
            data-testid="whats-new-share"
            className="w-full h-11 text-terracotta hover:text-terracotta/80 text-sm tracking-[0.18em] uppercase font-semibold hover:bg-terracotta/90 active:scale-[0.99] transition flex items-center justify-center gap-2"
          >
            <ShareNetwork size={14} weight="bold" />
            Share the update
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="whats-new-got-it"
            className="w-full h-11 text-forest hover:text-terracotta text-sm tracking-[0.18em] uppercase font-semibold hover:bg-forest/90 active:scale-[0.99] transition"
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
